import { useApiQuery } from '@/api/main/query'
import { DialogExport } from '@/entities/export'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import {
  dashboardMerchantApi,
  FinanceTableCard,
  MerchantDashboardCards,
  MerchantDashboardStatModel,
  StatisticMerchant,
  TopupsTableCard,
} from '@/features/dashboardMerchant'
import {
  DashboardPaymentType,
  dashboardTraderApi,
  TraderDashboardModel,
} from '@/features/dashboardTrader'
import {
  Statistic,
  TraderDashboardCards,
  TraderTopupsCard,
  TraderWithdrawsCard,
} from '@/features/dashboardTrader/ui'
import { BaseButton, PageTitle } from '@/shared/components'
import { getStorageItem, StatisticTabNames } from '@/shared/lib'
import { FileUp } from 'lucide-react'
import { useState } from 'react'
import { DateRange } from 'react-day-picker'

export const Dashboard = () => {
  const [tabName, setTabName] = useState<StatisticTabNames>('payIn')
  const [modalExportVisible, setModalExportVisible] = useState(false)
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const isTrader = userRole === Roles.Trader
  const isMerchant = userRole === Roles.Merchant

  // Выбор даты в календаре
  const getUtcStartOfDay = (date: Date) => {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    return utcDate.toISOString()
  }

  const getUtcEndOfDay = (date: Date) => {
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
    )
    return utcDate.toISOString()
  }
  const today = new Date()
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: today,
    to: today,
  })

  const startDate = dateRange?.from ? getUtcStartOfDay(dateRange.from) : undefined
  const endDate = dateRange?.to ? getUtcEndOfDay(dateRange.to) : undefined

  const paymentType =
    tabName === 'payIn' ? DashboardPaymentType.Topup : DashboardPaymentType.Withdraw

  // Статистика по мерчанту

  const merchantQueryParams = {
    startDate,
    endDate,
    paymentType,
  }

  const {
    data: dashboardMerchantStats,
    isLoading: isLoadingMerchantStat,
    error: errorMerchantStat,
  } = useApiQuery<MerchantDashboardStatModel>(
    [
      'merchant-dashboard-stat',
      merchantQueryParams.startDate,
      merchantQueryParams.endDate,
      paymentType,
    ],
    dashboardMerchantApi.baseKey,
    merchantQueryParams,
    {
      enabled: !!merchantQueryParams.startDate && isMerchant,
    }
  )

  // статистика по трейдеру

  const {
    data: traderStatData,
    isLoading: isTraderStatLoading,
    error: traderStatError,
  } = useApiQuery<TraderDashboardModel>(
    ['trader-dashboard-stat', startDate, endDate, paymentType],
    dashboardTraderApi.getStatistic,
    {
      paymentType,
      startDate,
      endDate,
    },
    {
      enabled: isTrader && !!startDate && !!endDate,
    }
  )

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range)
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex justify-between items-center">
        <PageTitle title="Дашборд" />
        <BaseButton
          variant="outlined"
          padding="none"
          radius="lg"
          className="px-4 py-3 !ring-card-border dark:!ring-card-border-dark !text-main-black dark:!text-white"
          onClick={() => setModalExportVisible(true)}
          icon={<FileUp strokeWidth={1.5} className="text-primary dark:text-primary-dark" />}
        >
          Экспорт
        </BaseButton>
      </div>

      {isTrader && (
        <>
          <Statistic
            statistic={traderStatData}
            isLoading={isTraderStatLoading}
            error={traderStatError}
            tabName={tabName}
            setTabName={setTabName}
            onDateChange={handleDateRangeChange}
          />
          <div className="gap-6 grid grid-cols-1 md:grid-cols-2">
            <TraderDashboardCards statistic={traderStatData} isLoading={isTraderStatLoading} />
            <TraderTopupsCard />
            <TraderWithdrawsCard />
          </div>
        </>
      )}

      {isMerchant && (
        <>
          <StatisticMerchant
            tabName={tabName}
            setTabName={setTabName}
            onDateChange={handleDateRangeChange}
            statistic={dashboardMerchantStats}
            isLoading={isLoadingMerchantStat}
            error={errorMerchantStat}
          />
          <div className="flex md:flex-row flex-col gap-6">
            <MerchantDashboardCards
              statistic={dashboardMerchantStats}
              isLoading={isLoadingMerchantStat}
              error={errorMerchantStat}
            />
          </div>
          <div className="gap-6 grid grid-cols-1 md:grid-cols-2">
            <FinanceTableCard />
            <TopupsTableCard />
          </div>
        </>
      )}

      <DialogExport
        modalExport={modalExportVisible}
        setModalExport={() => setModalExportVisible(false)}
      />
    </div>
  )
}
